import {
  ActorChip,
  AssetChip,
} from 'app/modules/auditService/components/Chips';
import {
  ActorChipProps,
  AssetChipComponentProps,
  AuditLogActor,
  AuditLogAsset,
  GenericChipProps,
  isDeadlineAuditLogAsset,
  AuditTrailAssetType,
} from 'app/modules/auditService/types';
import { DeadlineType } from 'app/modules/deadlines/models';
import { QueueType } from 'app/modules/queues/models';
import { U21Typography } from 'app/shared/u21-ui/components';
import pluralize from 'pluralize';
import { FC, Fragment, Key } from 'react';

interface ContentProps {
  actor: AuditLogActor;
  asset: AuditLogAsset;
  details: AuditLogAsset[];
}

const getActionKey = (action: AssetChipComponentProps | string): Key => {
  if (typeof action === 'string') {
    return action;
  }
  if (action.type === 'date') {
    return action.date;
  }
  if (action.type === 'generic') {
    return action.text;
  }
  if (action.type === 'deadline_internal') {
    return action.deadlineType;
  }
  return action.id;
};

const AuditLog = ({
  actor,
  action,
}: {
  actor: ActorChipProps;
  action: (AssetChipComponentProps | string)[];
}) => (
  <U21Typography>
    <ActorChip {...actor} />
    {action.map((logComponent) => (
      <Fragment key={getActionKey(logComponent)}>
        {' '}
        {typeof logComponent === 'string' ? (
          logComponent
        ) : (
          <AssetChip {...logComponent} />
        )}
      </Fragment>
    ))}
  </U21Typography>
);

export const AUDIT_TRAIL_ACTION_TO_CONTENT: Record<string, FC<ContentProps>> = {
  // Team Actions
  'team.create': ({ actor, asset: team }: ContentProps) => {
    return <AuditLog actor={actor} action={['created', team]} />;
  },
  'team.add_agent': ({ actor, asset: team, details: agents }: ContentProps) => {
    return <AuditLog actor={actor} action={['added', ...agents, 'to', team]} />;
  },
  'team.remove_agent': ({
    actor,
    asset: team,
    details: agents,
  }: ContentProps) => {
    return (
      <AuditLog actor={actor} action={['removed', ...agents, 'from', team]} />
    );
  },
  'team.add_permission': ({
    actor,
    asset: team,
    details: permissions,
  }: ContentProps) => {
    return permissions.length > 0 ? (
      <AuditLog
        actor={actor}
        action={[
          'added',
          ...permissions,
          pluralize('permission', permissions.length),
          'to',
          team,
        ]}
      />
    ) : null;
  },
  'team.remove_permission': ({
    actor,
    asset: team,
    details: permissions,
  }: ContentProps) => {
    return permissions.length > 0 ? (
      <AuditLog
        actor={actor}
        action={[
          'removed',
          ...permissions,
          pluralize('permission', permissions.length),
          'from',
          team,
        ]}
      />
    ) : null;
  },
  // AI Agent Actions
  'ai_agent.enable_task': ({ actor, asset, details }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={[
          'enabled',
          ...details,
          pluralize('task', details.length),
          'for',
          asset,
        ]}
      />
    );
  },
  'ai_agent.disable_task': ({ actor, asset, details }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={[
          'disabled',
          ...details,
          pluralize('task', details.length),
          'for',
          asset,
        ]}
      />
    );
  },
  'ai_agent.create_agent': ({ actor, asset }: ContentProps) => (
    <AuditLog actor={actor} action={['created', asset]} />
  ),
  'ai_agent.edit_agent': ({ actor, asset }: ContentProps) => (
    <AuditLog actor={actor} action={['edited', asset]} />
  ),
  'ai_agent.assign_to_queue': ({ actor, asset, details }: ContentProps) => {
    const queue =
      Array.isArray(details) && details.length > 0 ? details[0] : undefined;
    const queueId = queue?.id != null ? String(queue.id) : '';
    return (
      <AuditLog
        actor={actor}
        action={['assigned', asset, 'to queue', queueId]}
      />
    );
  },
  'ai_agent.edit_task': ({ actor, asset, details }: ContentProps) => (
    <AuditLog
      actor={actor}
      action={[
        'edited',
        ...(details ?? []),
        pluralize('task', details?.length ?? 1),
        'for',
        asset,
      ]}
    />
  ),
  // Case Actions
  'case.create': ({ actor, asset: caseAsset }: ContentProps) => {
    return <AuditLog actor={actor} action={['created', caseAsset]} />;
  },
  'case.open': ({ actor, asset: caseAsset }: ContentProps) => {
    return <AuditLog actor={actor} action={['opened', caseAsset]} />;
  },
  'case.close': ({ actor, asset: caseAsset }: ContentProps) => {
    return <AuditLog actor={actor} action={['closed', caseAsset]} />;
  },
  'case.in_review': ({ actor, asset: caseAsset }: ContentProps) => {
    return (
      <AuditLog actor={actor} action={['marked', caseAsset, 'for review']} />
    );
  },
  'case.add_entity': ({
    actor,
    asset: caseAsset,
    details: entities,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={[
          'added',
          ...entities,
          pluralize('entity', entities.length),
          'to',
          caseAsset,
        ]}
      />
    );
  },
  'case.remove_entity': ({
    actor,
    asset: caseAsset,
    details: entities,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={[
          'removed',
          ...entities,
          pluralize('entity', entities.length),
          'from',
          caseAsset,
        ]}
      />
    );
  },
  'case.add_instrument': ({
    actor,
    asset: caseAsset,
    details: instruments,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={[
          'added',
          ...instruments,
          pluralize('instrument', instruments.length),
          'to',
          caseAsset,
        ]}
      />
    );
  },
  'case.remove_instrument': ({
    actor,
    asset: caseAsset,
    details: instruments,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={[
          'removed',
          ...instruments,
          pluralize('instrument', instruments.length),
          'from',
          caseAsset,
        ]}
      />
    );
  },
  'case.add_alert': ({
    actor,
    asset: caseAsset,
    details: alerts,
  }: ContentProps) => {
    return (
      <AuditLog actor={actor} action={['added', ...alerts, 'to', caseAsset]} />
    );
  },
  'case.remove_alert': ({
    actor,
    asset: caseAsset,
    details: alerts,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={['removed', ...alerts, 'from', caseAsset]}
      />
    );
  },
  'case.create_sar': ({ actor, asset: caseAsset, details }: ContentProps) => {
    const [filing] = details;
    return (
      <AuditLog actor={actor} action={['created', filing, 'from', caseAsset]} />
    );
  },
  'case.narrative_create': ({ actor, asset: caseAsset }: ContentProps) => {
    return (
      <AuditLog actor={actor} action={['added a narrative to', caseAsset]} />
    );
  },
  'case.narrative_edit': ({ actor, asset: caseAsset }: ContentProps) => {
    return (
      <AuditLog actor={actor} action={['edited a narrative on', caseAsset]} />
    );
  },
  'case.narrative_delete': ({ actor, asset: caseAsset }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={['deleted a narrative from', caseAsset]}
      />
    );
  },
  'case.assign': ({ actor, asset: caseAsset, details }: ContentProps) => {
    const [agent] = details;
    const typedAgent: AuditLogAsset = agent?.type
      ? agent
      : { type: 'agent', id: -1, name: 'No one', isMissing: false }; // Handle assigments to "no one"
    return (
      <AuditLog
        actor={actor}
        action={['assigned', caseAsset, 'to', typedAgent]}
      />
    );
  },
  'case.change_queue': ({ actor, asset: caseAsset, details }: ContentProps) => {
    const [queue] = details;
    if (!queue) return null;
    const caseQueue: AssetChipComponentProps = {
      ...queue,
      type: 'queue_internal',
      queueType: QueueType.CASE_QUEUE,
    };
    return (
      <AuditLog actor={actor} action={['moved', caseAsset, 'to', caseQueue]} />
    );
  },
  'case.add_tag': ({
    actor,
    asset: caseAsset,
    details: tags,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={[
          'added',
          ...tags,
          pluralize('tag', tags.length),
          'to',
          caseAsset,
        ]}
      />
    );
  },
  'case.remove_tag': ({
    actor,
    asset: caseAsset,
    details: tags,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={[
          'removed',
          ...tags,
          pluralize('tag', tags.length),
          'from',
          caseAsset,
        ]}
      />
    );
  },
  'case.add_txn_event': ({
    actor,
    asset: caseAsset,
    details: events,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={[
          'added',
          ...events,
          pluralize('transaction', events.length),
          'to',
          caseAsset,
        ]}
      />
    );
  },
  'case.remove_txn_event': ({
    actor,
    asset: caseAsset,
    details: events,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={[
          'removed',
          ...events,
          pluralize('transaction', events.length),
          'from',
          caseAsset,
        ]}
      />
    );
  },
  'case.add_action_event': ({
    actor,
    asset: caseAsset,
    details: events,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={[
          'added',
          ...events,
          pluralize('action', events.length),
          'to',
          caseAsset,
        ]}
      />
    );
  },
  'case.remove_action_event': ({
    actor,
    asset: caseAsset,
    details: events,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={[
          'removed',
          ...events,
          pluralize('action', events.length),
          'from',
          caseAsset,
        ]}
      />
    );
  },
  'case.add_attachment': ({
    actor,
    asset: caseAsset,
    details: attachments,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={['uploaded', ...attachments, 'to', caseAsset]}
      />
    );
  },
  'case.remove_attachment': ({
    actor,
    asset: caseAsset,
    details: attachments,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={['removed', ...attachments, 'from', caseAsset]}
      />
    );
  },
  'case.disposition': ({ actor, asset: caseAsset, details }: ContentProps) => {
    const [disposition] = details;
    return disposition?.name ? (
      <AuditLog
        actor={actor}
        action={['dispositioned', caseAsset, 'as', disposition]}
      />
    ) : (
      <AuditLog actor={actor} action={['dispositioned', caseAsset]} />
    );
  },
  'case.subdisposition': ({
    actor,
    asset: caseAsset,
    details: subdispositions,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={['subdispositioned', caseAsset, 'as', ...subdispositions]}
      />
    );
  },
  'case.add_watcher': ({
    actor,
    asset: caseAsset,
    details: watchers,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={[
          'added',
          ...watchers,
          'as',
          watchers.length > 1 ? 'watchers' : 'a watcher',
          'for',
          caseAsset,
        ]}
      />
    );
  },
  'case.remove_watcher': ({
    actor,
    asset: caseAsset,
    details: watchers,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={[
          'removed',
          ...watchers,
          'as',
          watchers.length > 1 ? 'watchers' : 'a watcher',
          'for',
          caseAsset,
        ]}
      />
    );
  },
  'case.trigger_action': ({
    actor,
    asset: caseAsset,
    details: actions,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={['triggered', ...actions, 'on', caseAsset]}
      />
    );
  },
  'case.set_deadline': ({ actor, asset: caseAsset, details }: ContentProps) => {
    const [deadline] = details;
    if (!deadline) return null;
    if (!isDeadlineAuditLogAsset(deadline)) return null;
    const { custom_data: customData } = deadline;
    const { date, type } = customData ?? {};

    if (type === DeadlineType.NO_DEADLINE) {
      return (
        <AuditLog actor={actor} action={['removed deadline from', caseAsset]} />
      );
    }

    const deadlineAsset: AssetChipComponentProps = {
      type: 'deadline_internal',
      deadlineType: type,
    };
    const dateAsset: AssetChipComponentProps = { type: 'date', date };
    return (
      <AuditLog
        actor={actor}
        action={['set', deadlineAsset, 'of', dateAsset, 'to', caseAsset]}
      />
    );
  },
  'case.update_custom_data': ({ actor, asset: caseAsset }: ContentProps) => {
    return (
      <AuditLog actor={actor} action={['updated custom data on', caseAsset]} />
    );
  },
  'case.update_disposition_notes': ({
    actor,
    asset: caseAsset,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={['updated disposition notes on', caseAsset]}
      />
    );
  },
  'case.update_txn_fraud_metadata': ({
    actor,
    asset: caseAsset,
  }: ContentProps) => {
    return (
      <AuditLog actor={actor} action={['updated fraud losses on', caseAsset]} />
    );
  },
  'case.set_start_date': ({ actor, asset: caseAsset }: ContentProps) => {
    return <AuditLog actor={actor} action={['set start date of', caseAsset]} />;
  },

  // Alerts
  'alert.create': ({ actor, asset: alert }: ContentProps) => {
    return <AuditLog actor={actor} action={['created', alert]} />;
  },
  'alert.open': ({ actor, asset: alert }: ContentProps) => {
    return <AuditLog actor={actor} action={['opened', alert]} />;
  },
  'alert.close': ({ actor, asset: alert }: ContentProps) => {
    return <AuditLog actor={actor} action={['closed', alert]} />;
  },
  'alert.in_review': ({ actor, asset: alert }: ContentProps) => {
    return <AuditLog actor={actor} action={['marked', alert, 'for review']} />;
  },
  'alert.create_sar': ({ actor, asset: alert, details }: ContentProps) => {
    const [sar] = details;
    return <AuditLog actor={actor} action={['created', sar, 'from', alert]} />;
  },
  'alert.narrative_create': ({ actor, asset: alert }: ContentProps) => {
    return <AuditLog actor={actor} action={['added a narrative to', alert]} />;
  },
  'alert.narrative_edit': ({ actor, asset: alert }: ContentProps) => {
    return <AuditLog actor={actor} action={['edited a narrative on', alert]} />;
  },
  'alert.narrative_delete': ({ actor, asset: alert }: ContentProps) => {
    return (
      <AuditLog actor={actor} action={['deleted a narrative from', alert]} />
    );
  },
  'alert.start_ai_review': ({ actor, asset: alert }: ContentProps) => {
    return <AuditLog actor={actor} action={['started AI review for', alert]} />;
  },
  'alert.create_case': ({ actor, details }: ContentProps) => {
    const [createdCase] = details;
    return <AuditLog actor={actor} action={['created', createdCase]} />;
  },
  'alert.assign': ({ actor, asset: alert, details }: ContentProps) => {
    const [agent] = details;
    const typedAgent: AuditLogAsset = agent?.type
      ? agent
      : { type: 'agent', id: -1, name: 'No one', isMissing: false }; // Handle assigments to "no one"
    return (
      <AuditLog actor={actor} action={['assigned', alert, 'to', typedAgent]} />
    );
  },
  'alert.change_queue': ({ actor, asset: alert, details }: ContentProps) => {
    const [queue] = details;
    if (!queue) return null;
    const alertQueue: AssetChipComponentProps = {
      ...queue,
      type: 'queue_internal',
      queueType: QueueType.ALERT_QUEUE,
    };
    return (
      <AuditLog actor={actor} action={['moved', alert, 'to', alertQueue]} />
    );
  },
  'alert.add_rule': ({ actor, asset: alert, details: rules }: ContentProps) => {
    return <AuditLog actor={actor} action={['added', ...rules, 'to', alert]} />;
  },
  'alert.remove_rule': ({
    actor,
    asset: alert,
    details: rules,
  }: ContentProps) => {
    return (
      <AuditLog actor={actor} action={['removed', ...rules, 'from', alert]} />
    );
  },
  'alert.add_tag': ({ actor, asset: alert, details: tags }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={['added', ...tags, pluralize('tag', tags.length), 'to', alert]}
      />
    );
  },
  'alert.remove_tag': ({
    actor,
    asset: alert,
    details: tags,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={[
          'removed',
          ...tags,
          pluralize('tag', tags.length),
          'from',
          alert,
        ]}
      />
    );
  },
  'alert.add_attachment': ({
    actor,
    asset: alert,
    details: attachments,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={['uploaded', ...attachments, 'to', alert]}
      />
    );
  },
  'alert.remove_attachment': ({
    actor,
    asset: alert,
    details: attachments,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={['removed', ...attachments, 'from', alert]}
      />
    );
  },
  'alert.disposition': ({ actor, asset: alert, details }: ContentProps) => {
    const [disposition] = details;
    return disposition?.name ? (
      <AuditLog
        actor={actor}
        action={['dispositioned', alert, 'as', disposition]}
      />
    ) : (
      <AuditLog actor={actor} action={['dispositioned', alert]} />
    );
  },
  'alert.subdisposition': ({
    actor,
    asset: alert,
    details: subdispositions,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={['subdispositioned', alert, 'as', ...subdispositions]}
      />
    );
  },
  'alert.add_entity': ({
    actor,
    asset: alert,
    details: entities,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={[
          'added',
          ...entities,
          pluralize('entity', entities.length),
          'to',
          alert,
        ]}
      />
    );
  },
  'alert.remove_entity': ({
    actor,
    asset: alert,
    details: entities,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={[
          'removed',
          ...entities,
          pluralize('entity', entities.length),
          'from',
          alert,
        ]}
      />
    );
  },
  'alert.add_txn_event': ({
    actor,
    asset: alert,
    details: events,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={[
          'added',
          ...events,
          pluralize('transaction', events.length),
          'to',
          alert,
        ]}
      />
    );
  },
  'alert.remove_txn_event': ({
    actor,
    asset: alert,
    details: events,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={[
          'removed',
          ...events,
          pluralize('transaction', events.length),
          'from',
          alert,
        ]}
      />
    );
  },
  'alert.add_action_event': ({
    actor,
    asset: alert,
    details: events,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={[
          'added',
          ...events,
          pluralize('action', events.length),
          'to',
          alert,
        ]}
      />
    );
  },
  'alert.remove_action_event': ({
    actor,
    asset: alert,
    details: events,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={[
          'removed',
          ...events,
          pluralize('action', events.length),
          'from',
          alert,
        ]}
      />
    );
  },
  'alert.add_instrument': ({
    actor,
    asset: alert,
    details: instruments,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={[
          'added',
          ...instruments,
          pluralize('instrument', instruments.length),
          'to',
          alert,
        ]}
      />
    );
  },
  'alert.remove_instrument': ({
    actor,
    asset: alert,
    details: instruments,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={[
          'removed',
          ...instruments,
          pluralize('instrument', instruments.length),
          'from',
          alert,
        ]}
      />
    );
  },
  'alert.trigger_action': ({
    actor,
    asset: alert,
    details: actions,
  }: ContentProps) => {
    return (
      <AuditLog actor={actor} action={['triggered', ...actions, 'on', alert]} />
    );
  },
  'alert.set_deadline': ({ actor, asset: alert, details }: ContentProps) => {
    const [deadline] = details;
    if (!deadline) return null;
    if (!isDeadlineAuditLogAsset(deadline)) return null;
    const { custom_data: customData } = deadline;
    const { date, type } = customData ?? {};

    if (type === DeadlineType.NO_DEADLINE) {
      return (
        <AuditLog actor={actor} action={['removed deadline from', alert]} />
      );
    }

    const deadlineAsset: AssetChipComponentProps = {
      type: 'deadline_internal',
      deadlineType: type,
    };
    const dateAsset: AssetChipComponentProps = { type: 'date', date };
    return (
      <AuditLog
        actor={actor}
        action={['set', deadlineAsset, 'of', dateAsset, 'to', alert]}
      />
    );
  },
  'alert.update_custom_data': ({ actor, asset: alert }: ContentProps) => {
    return (
      <AuditLog actor={actor} action={['updated custom data on', alert]} />
    );
  },
  'alert.update_disposition_notes': ({ actor, asset: alert }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={['updated disposition notes on', alert]}
      />
    );
  },
  'alert.run_ai_investigation': ({ actor, asset: alert }: ContentProps) => {
    return (
      <AuditLog actor={actor} action={['ran AI investigation for', alert]} />
    );
  },

  // CTRs
  'fincen_ctr.create': ({ actor, asset: ctr, details }: ContentProps) => {
    const [fromArticle] = details;
    return fromArticle ? (
      <AuditLog actor={actor} action={['created', ctr, 'from', fromArticle]} />
    ) : (
      <AuditLog actor={actor} action={['created', ctr]} />
    );
  },
  'fincen_ctr.edit': ({ actor, asset: ctr }: ContentProps) => {
    // TODO show the edited fields
    return <AuditLog actor={actor} action={['edited', ctr]} />;
  },
  'fincen_ctr.lock': ({ actor, asset: ctr }: ContentProps) => {
    const status: GenericChipProps = { type: 'generic', text: 'Ready to file' };
    return (
      <AuditLog
        actor={actor}
        action={['updated the status of', ctr, 'to', status]}
      />
    );
  },
  'fincen_ctr.unlock': ({ actor, asset: ctr }: ContentProps) => {
    const status: GenericChipProps = { type: 'generic', text: 'In progress' };
    return (
      <AuditLog
        actor={actor}
        action={['updated the status of', ctr, 'to', status]}
      />
    );
  },
  'fincen_ctr.create_batch': ({ actor, asset: ctr, details }: ContentProps) => {
    const [batch] = details;
    // TODO add a chip, we don't have a page dedicated to batches
    return (
      <AuditLog
        actor={actor}
        action={[`created batch ${batch.id} from`, ctr]}
      />
    );
  },
  'fincen_ctr.add_attachment': ({
    actor,
    asset: ctr,
    details: attachments,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={['uploaded', ...attachments, 'to', ctr]}
      />
    );
  },
  'fincen_ctr.remove_attachment': ({
    actor,
    asset: ctr,
    details: attachments,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={['removed', ...attachments, 'from', ctr]}
      />
    );
  },
  'fincen_ctr.submit': ({ actor, asset: ctr }: ContentProps) => {
    return <AuditLog actor={actor} action={['submitted', ctr]} />;
  },
  // GoAML Actions
  'goaml.create': ({ actor, asset: filing }: ContentProps) => {
    return <AuditLog actor={actor} action={['created', filing]} />;
  },
  'entity.remove_attachment': ({
    actor,
    asset: entity,
    details: attachments,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={['removed', ...attachments, 'from', entity]}
      />
    );
  },
  'entity.add_attachment': ({
    actor,
    asset: entity,
    details: attachments,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={['uploaded', ...attachments, 'to', entity]}
      />
    );
  },
  'entity.remove_all_attachments': ({ actor, asset: entity }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={['removed all attachments from', entity]}
      />
    );
  },
  'entity.whitelist': ({ actor, asset: entity, details }: ContentProps) => {
    const [rule] = details;
    return (
      <AuditLog actor={actor} action={['silenced', entity, 'for', rule]} />
    );
  },
  'entity.unwhitelist': ({ actor, asset: entity, details }: ContentProps) => {
    const [rule] = details;
    return (
      <AuditLog actor={actor} action={['unsilenced', entity, 'for', rule]} />
    );
  },
  'entity.global_whitelist': ({ actor, asset: entity }: ContentProps) => {
    return (
      <AuditLog actor={actor} action={['silenced', entity, 'for all rules']} />
    );
  },
  'entity.global_unwhitelist': ({ actor, asset: entity }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={['unsilenced', entity, 'for all rules']}
      />
    );
  },
  'entity.override_risk_rating': ({ actor, details }: ContentProps) => {
    const [riskModel] = details;
    return (
      <AuditLog actor={actor} action={['overrode', riskModel, 'risk rating']} />
    );
  },
  'entity.clear_risk_rating_override': ({ actor }: ContentProps) => {
    return (
      <AuditLog actor={actor} action={['cleared the risk rating override']} />
    );
  },
  'entity.risk_rating_override_expired': ({ actor }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={['cleared risk rating override due to expiration']}
      />
    );
  },
  // Risk Ratings
  'risk_model.edit': ({ actor, asset: riskModel }: ContentProps) => {
    return <AuditLog actor={actor} action={['edited', riskModel]} />;
  },
  'risk_model.activate': ({ actor, asset: riskModel }: ContentProps) => {
    return <AuditLog actor={actor} action={['activated', riskModel]} />;
  },
  'risk_model.deactivate': ({ actor, asset: riskModel }: ContentProps) => {
    return <AuditLog actor={actor} action={['deactivated', riskModel]} />;
  },
  'risk_model.create': ({ actor, asset: riskModel }: ContentProps) => {
    return <AuditLog actor={actor} action={['created', riskModel]} />;
  },
  'risk_model.validate': ({ actor, asset: riskModel }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={['started a validation for', riskModel]}
      />
    );
  },
  'risk_model.create_entity_segment': ({
    actor,
    asset: riskModel,
    details: entitySegment,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={['added', ...entitySegment, 'to', riskModel]}
      />
    );
  },
  'risk_model.update_entity_segment': ({
    actor,
    asset: riskModel,
    details: entitySegment,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={['updated', ...entitySegment, 'on', riskModel]}
      />
    );
  },
  'risk_model.delete_entity_segment': ({
    actor,
    asset: riskModel,
    details: entitySegment,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={['deleted', ...entitySegment, 'from', riskModel]}
      />
    );
  },
  'risk_model.override_risk_rating': ({ actor, details }: ContentProps) => {
    const [entity] = details;
    return (
      <AuditLog actor={actor} action={['overrode', entity, 'risk rating']} />
    );
  },
  'risk_model.clear_risk_rating_override': ({
    actor,
    details,
  }: ContentProps) => {
    const [entity] = details;
    return (
      <AuditLog
        actor={actor}
        action={['cleared', entity, 'risk rating override']}
      />
    );
  },
  'risk_model.risk_rating_override_expired': ({
    actor,
    details,
  }: ContentProps) => {
    const [entity] = details;
    return (
      <AuditLog
        actor={actor}
        action={[
          'cleared risk rating override for',
          entity,
          'due to expiration',
        ]}
      />
    );
  },

  // Rules
  'rule.create': ({ actor, asset: rule }: ContentProps) => {
    return <AuditLog actor={actor} action={['created', rule]} />;
  },
  'rule.deactivate': ({ actor, asset: rule }: ContentProps) => {
    return <AuditLog actor={actor} action={['deactivated', rule]} />;
  },
  'rule.deploy': ({ actor, asset: rule }: ContentProps) => {
    return <AuditLog actor={actor} action={['deployed', rule]} />;
  },
  'rule.shadow_deploy': ({ actor, asset: rule }: ContentProps) => {
    return (
      <AuditLog actor={actor} action={['deployed', rule, 'in shadow mode']} />
    );
  },
  'rule.duplicate': ({ actor, asset: rule }: ContentProps) => {
    return <AuditLog actor={actor} action={['duplicated', rule]} />;
  },
  'rule.edit': ({ actor, asset: rule }: ContentProps) => {
    // Legacy action, not logged anywhere
    return <AuditLog actor={actor} action={['edited', rule]} />;
  },
  'rule.set_title': ({ actor, asset: rule }: ContentProps) => {
    return <AuditLog actor={actor} action={['updated title on', rule]} />;
  },
  'rule.set_description': ({ actor, asset: rule }: ContentProps) => {
    return <AuditLog actor={actor} action={['updated description on', rule]} />;
  },
  'rule.set_execution_delay_in_minutes': ({
    actor,
    asset: rule,
  }: ContentProps) => {
    return <AuditLog actor={actor} action={['updated the delay on', rule]} />;
  },
  'rule.set_decision': ({ actor, asset: rule }: ContentProps) => {
    return <AuditLog actor={actor} action={['updated decision on', rule]} />;
  },

  'rule.publish': ({ actor, asset: rule }: ContentProps) => {
    return <AuditLog actor={actor} action={['published', rule]} />;
  },
  'rule.set_priority': ({ actor, asset: rule }: ContentProps) => {
    return (
      <AuditLog actor={actor} action={['updated rule priority on', rule]} />
    );
  },
  'rule.set_generates_alerts': ({ actor, asset: rule }: ContentProps) => {
    return (
      <AuditLog actor={actor} action={['enabled alert generation on', rule]} />
    );
  },
  'rule.unset_generates_alerts': ({ actor, asset: rule }: ContentProps) => {
    return (
      <AuditLog actor={actor} action={['disabled alert generation on', rule]} />
    );
  },
  'rule.set_content': ({ actor, asset: rule }: ContentProps) => {
    return <AuditLog actor={actor} action={['updated content on', rule]} />;
  },
  'rule.add_queue': ({ actor, asset: rule, details }: ContentProps) => {
    const [queue] = details;
    if (!queue) return null;
    const alertQueue: AssetChipComponentProps = {
      ...queue,
      type: 'queue_internal',
      queueType: QueueType.ALERT_QUEUE,
    };
    return (
      <AuditLog actor={actor} action={['added', alertQueue, 'to', rule]} />
    );
  },
  'rule.remove_queue': ({ actor, asset: rule, details }: ContentProps) => {
    const [queue] = details;
    if (!queue) return null;
    const alertQueue: AssetChipComponentProps = {
      ...queue,
      type: 'queue_internal',
      queueType: QueueType.ALERT_QUEUE,
    };
    return (
      <AuditLog actor={actor} action={['removed', alertQueue, 'from', rule]} />
    );
  },
  'rule.add_tag': ({ actor, asset: rule, details: tags }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={['added', ...tags, pluralize('tag', tags.length), 'to', rule]}
      />
    );
  },
  'rule.remove_tag': ({ actor, asset: rule, details: tags }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={[
          'removed',
          ...tags,
          pluralize('tag', tags.length),
          'from',
          rule,
        ]}
      />
    );
  },

  // Rule Flows
  'rule_flow.create': ({ actor, asset: flow }: ContentProps) => {
    return <AuditLog actor={actor} action={['created', flow]} />;
  },
  'rule_flow.activate': ({ actor, asset: flow }: ContentProps) => {
    return <AuditLog actor={actor} action={['activated', flow]} />;
  },
  'rule_flow.archive': ({ actor, asset: flow }: ContentProps) => {
    return <AuditLog actor={actor} action={['archived', flow]} />;
  },

  // FinCEN SARs
  'sar.add_attachment': ({
    actor,
    asset: sar,
    details: attachments,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={['uploaded', ...attachments, 'to', sar]}
      />
    );
  },
  'sar.create': ({ actor, asset: sar }: ContentProps) => {
    return <AuditLog actor={actor} action={['created', sar]} />;
  },
  'sar.edit': ({ actor, asset: sar }: ContentProps) => {
    return <AuditLog actor={actor} action={['edited', sar]} />;
  },
  'sar.set_deadline': ({ actor, asset: sar, details }: ContentProps) => {
    const [deadline] = details;
    if (!deadline) return null;
    if (!isDeadlineAuditLogAsset(deadline)) return null;
    const { custom_data: customData } = deadline;
    const { date, type } = customData ?? {};

    if (type === DeadlineType.NO_DEADLINE) {
      return <AuditLog actor={actor} action={['removed deadline from', sar]} />;
    }

    const deadlineAsset: AssetChipComponentProps = {
      type: 'deadline_internal',
      deadlineType: type,
    };
    const dateAsset: AssetChipComponentProps = { type: 'date', date };
    return (
      <AuditLog
        actor={actor}
        action={['set', deadlineAsset, 'of', dateAsset, 'to', sar]}
      />
    );
  },
  'sar.submit': ({ actor, asset: sar }: ContentProps) => {
    return <AuditLog actor={actor} action={['submitted', sar]} />;
  },
  'sar.remove_attachment': ({
    actor,
    asset: sar,
    details: attachments,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={['removed', ...attachments, 'from', sar]}
      />
    );
  },
  'sar.add_entity': ({
    actor,
    asset: sar,
    details: entities,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={[
          'added',
          ...entities,
          pluralize('entity', entities.length),
          'to',
          sar,
        ]}
      />
    );
  },
  'sar.remove_entity': ({
    actor,
    asset: sar,
    details: entities,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={[
          'removed',
          ...entities,
          pluralize('entity', entities.length),
          'from',
          sar,
        ]}
      />
    );
  },
  'sar.assign': ({ actor, asset: sar, details }: ContentProps) => {
    const [agent] = details;
    const typedAgent: AuditLogAsset = agent?.type
      ? agent
      : { type: 'agent', id: -1, name: 'No one', isMissing: false }; // Handle assigments to "no one"
    return (
      <AuditLog actor={actor} action={['assigned', sar, 'to', typedAgent]} />
    );
  },
  // Deprecated, left in for backwards compatibility
  'sar.reassign': ({ actor, asset: sar, details }: ContentProps) => {
    const [agent] = details;
    const typedAgent: AuditLogAsset = agent?.type
      ? agent
      : { type: 'agent', id: -1, name: 'No one', isMissing: false }; // Handle assigments to "no one"
    return (
      <AuditLog actor={actor} action={['assigned', sar, 'to', typedAgent]} />
    );
  },
  'sar.requeue': ({ actor, asset: sar, details }: ContentProps) => {
    const [queue] = details;
    if (!queue) return null;
    const sarQueue: AssetChipComponentProps = {
      ...queue,
      type: 'queue_internal',
      queueType: QueueType.SAR_QUEUE,
    };
    return <AuditLog actor={actor} action={['moved', sar, 'to', sarQueue]} />;
  },
  'sar.duplicate': ({ actor, asset: sar }: ContentProps) => {
    return <AuditLog actor={actor} action={['duplicated', sar]} />;
  },
  'sar.lock': ({ actor, asset: sar }: ContentProps) => {
    const status: GenericChipProps = {
      type: 'generic',
      text: 'Ready for review',
    };
    return (
      <AuditLog
        actor={actor}
        action={['updated the status of', sar, 'to', status]}
      />
    );
  },
  'sar.unlock': ({ actor, asset: sar }: ContentProps) => {
    const status: GenericChipProps = { type: 'generic', text: 'In progress' };
    return (
      <AuditLog
        actor={actor}
        action={['updated the status of', sar, 'to', status]}
      />
    );
  },
  'instrument.lock': ({
    actor,
    asset: instrument,
    details: blockedReasons,
  }: ContentProps) => {
    const status: GenericChipProps = { type: 'generic', text: 'Locked' };

    const reason: GenericChipProps | null =
      blockedReasons === undefined || blockedReasons.length === 0
        ? null
        : { type: 'generic', text: blockedReasons[0].name };
    const detailList = !reason ? [] : ['with reason', reason];
    return (
      <AuditLog
        actor={actor}
        action={['changed', instrument, 'to', status, ...detailList]}
      />
    );
  },
  'instrument.unlock': ({ actor, asset: instrument }: ContentProps) => {
    const status: GenericChipProps = { type: 'generic', text: 'Unlocked' };

    return (
      <AuditLog actor={actor} action={['changed', instrument, 'to', status]} />
    );
  },
  'instrument.deactivate': ({
    actor,
    asset: instrument,
    details: blockedReasons,
  }: ContentProps) => {
    const status: GenericChipProps = { type: 'generic', text: 'Deactivated' };
    const reason: GenericChipProps | null =
      blockedReasons === undefined || blockedReasons.length === 0
        ? null
        : { type: 'generic', text: blockedReasons[0].name };
    const detailList = !reason ? [] : ['with reason', reason];
    return (
      <AuditLog
        actor={actor}
        action={['changed', instrument, 'to', status, ...detailList]}
      />
    );
  },
  // FINTRAC STR Actions
  'fintrac_str.create': ({ actor, asset: fintracStr }: ContentProps) => {
    return <AuditLog actor={actor} action={['created', fintracStr]} />;
  },
  'fintrac_str.edit': ({ actor, asset: fintracStr }: ContentProps) => {
    return <AuditLog actor={actor} action={['edited', fintracStr]} />;
  },
  'fintrac_str.set_deadline': ({
    actor,
    asset: fintracStr,
    details,
  }: ContentProps) => {
    const [deadline] = details;
    if (!deadline) return null;
    if (!isDeadlineAuditLogAsset(deadline)) return null;
    const { custom_data: customData } = deadline;
    const { date, type } = customData ?? {};

    if (type === DeadlineType.NO_DEADLINE) {
      return (
        <AuditLog
          actor={actor}
          action={['removed deadline from', fintracStr]}
        />
      );
    }

    const deadlineAsset: AssetChipComponentProps = {
      type: 'deadline_internal',
      deadlineType: type,
    };
    const dateAsset: AssetChipComponentProps = { type: 'date', date };
    return (
      <AuditLog
        actor={actor}
        action={['set', deadlineAsset, 'of', dateAsset, 'to', fintracStr]}
      />
    );
  },
  'fintrac_str.submit': ({ actor, asset: fintracStr }: ContentProps) => {
    return <AuditLog actor={actor} action={['submitted', fintracStr]} />;
  },
  'fintrac_str.assign': ({
    actor,
    asset: fintracStr,
    details,
  }: ContentProps) => {
    const [agent] = details;
    const typedAgent: AuditLogAsset = agent?.type
      ? agent
      : { type: 'agent', id: -1, name: 'No one', isMissing: false }; // Handle assigments to "no one"
    return (
      <AuditLog
        actor={actor}
        action={['assigned', fintracStr, 'to', typedAgent]}
      />
    );
  },
  'fintrac_str.requeue': ({
    actor,
    asset: fintracStr,
    details,
  }: ContentProps) => {
    const [queue] = details;
    if (!queue) return null;
    const filingQueue: AssetChipComponentProps = {
      ...queue,
      type: 'queue_internal',
      queueType: QueueType.SAR_QUEUE,
    };
    return (
      <AuditLog
        actor={actor}
        action={['moved', fintracStr, 'to', filingQueue]}
      />
    );
  },
  'fintrac_str.duplicate': ({ actor, asset: fintracStr }: ContentProps) => {
    return <AuditLog actor={actor} action={['duplicated', fintracStr]} />;
  },
  'fintrac_str.lock': ({ actor, asset: fintracStr }: ContentProps) => {
    const status: GenericChipProps = {
      type: 'generic',
      text: 'Ready for review',
    };
    return (
      <AuditLog
        actor={actor}
        action={['updated the status of', fintracStr, 'to', status]}
      />
    );
  },
  'fintrac_str.unlock': ({ actor, asset: fintracStr }: ContentProps) => {
    const status: GenericChipProps = { type: 'generic', text: 'In progress' };
    return (
      <AuditLog
        actor={actor}
        action={['updated the status of', fintracStr, 'to', status]}
      />
    );
  },
  // AI Agents Narrative Instructions
  'ai_narrative_instructions.create': ({
    actor,
    asset: narrativeInstructions,
  }: ContentProps) => {
    return (
      <AuditLog actor={actor} action={['created', narrativeInstructions]} />
    );
  },
  'ai_narrative_instructions.edit': ({
    actor,
    asset: narrativeInstructions,
  }: ContentProps) => {
    return (
      <AuditLog actor={actor} action={['modified', narrativeInstructions]} />
    );
  },
  'ai_narrative_instructions.delete': ({ actor }: ContentProps) => {
    return (
      <AuditLog actor={actor} action={['reset to default instructions']} />
    );
  },
  // Banking Actions
  'instrument.block': ({ actor, asset }: ContentProps) => {
    return <AuditLog actor={actor} action={['blocked card for', asset]} />;
  },
  'instrument.activate': ({ actor, asset }: ContentProps) => {
    return <AuditLog actor={actor} action={['activated card for', asset]} />;
  },
  'txn_event.stop': ({ actor, asset }: ContentProps) => {
    return <AuditLog actor={actor} action={['stopped payment for', asset]} />;
  },
  'txn_event.hold': ({ actor, asset }: ContentProps) => {
    return <AuditLog actor={actor} action={['checked hold for', asset]} />;
  },
  // Watchlist Entity Actions
  // When rules are present in details, this is an onboarding screen triggered
  // by the API. Without rules, it's an ongoing monitoring screen.
  'watchlist_entity.initial_screen': ({
    actor,
    asset: entity,
    details: rules,
  }: ContentProps) => {
    return rules.length > 0 ? (
      <AuditLog
        actor={actor}
        action={['initiated onboarding screening on', entity, 'for', ...rules]}
      />
    ) : (
      <AuditLog actor={actor} action={['initiated screening on', entity]} />
    );
  },
  'watchlist_entity.rescreen': ({ actor, asset: entity }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={[
          'initiated re-screening on',
          entity,
          'due to updated screening data',
        ]}
      />
    );
  },
  // When rules are present in details, this is an onboarding screen triggered
  // by the API. Without rules, it's an ongoing monitoring screen.
  'watchlist_entity.screen_complete': ({
    actor,
    asset: entity,
    details: rules,
  }: ContentProps) => {
    return rules.length > 0 ? (
      <AuditLog
        actor={actor}
        action={['completed onboarding screening on', entity, 'for', ...rules]}
      />
    ) : (
      <AuditLog actor={actor} action={['completed screening on', entity]} />
    );
  },
  'watchlist_entity.params_no_match_complete': ({
    actor,
    asset: entity,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={[
          'skipped screening on',
          entity,
          'due to missing required data (e.g. name)',
        ]}
      />
    );
  },
  // Details always contain an alert. When rules are also present, this is from
  // onboarding screening (actor is API). Without rules, it's from ongoing
  // monitoring (actor is a rule).
  'watchlist_entity.alert_created': ({
    actor,
    asset: entity,
    details,
  }: ContentProps) => {
    const alert = details.find((d) => d.type === 'alert')!;
    const rules = details.filter((d) => d.type === 'rule');
    return rules.length > 0 ? (
      <AuditLog
        actor={actor}
        action={[
          'created',
          alert,
          'from onboarding screening on',
          entity,
          'for',
          ...rules,
        ]}
      />
    ) : (
      <AuditLog
        actor={actor}
        action={['created', alert, 'from screening on', entity]}
      />
    );
  },
  'watchlist_entity.webhook_alert_created': ({
    actor,
    asset: entity,
    details,
  }: ContentProps) => {
    const [alert] = details;
    return (
      <AuditLog
        actor={actor}
        action={['created', alert, 'from ongoing monitoring on', entity]}
      />
    );
  },
  'watchlist_entity.onboarding_transition_to_ongoing': ({
    actor,
    asset: entity,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={['transitioned', entity, 'to ongoing monitoring']}
      />
    );
  },
  'watchlist_entity.ongoing_monitoring_enable': ({
    actor,
    asset: entity,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={['enabled ongoing monitoring for', entity]}
      />
    );
  },
  'watchlist_entity.ongoing_monitoring_disable': ({
    actor,
    asset: entity,
  }: ContentProps) => {
    return (
      <AuditLog
        actor={actor}
        action={['disabled ongoing monitoring for', entity]}
      />
    );
  },
};

export const getFormattedAssetName = (asset: AuditTrailAssetType): string => {
  switch (asset) {
    case 'fincen_ctr':
      return 'CTR';
    case 'sar':
      return 'SAR';
    case 'goaml':
      return 'GoAML filing';
    case 'risk_model':
      return 'risk model';
    case 'rule_flow':
      return 'flow';
    case 'watchlist_entity':
      return 'screening';
    default:
      return asset;
  }
};
